import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GetUserData } from '../components/UserContext';
import DonutChart from '../components/DonutChart';
import { walletServices } from '../services/WalletServices';
import { planServices } from '../services/PlanServices';
import InvestmentProgress from '../components/InvestmentProgress';

const Dashboard = () => {
  const [walletData, setWalletData] = useState({});
  const [walletTransactionData, setWalletTransactionData] = useState([]);
  const id = localStorage.getItem('id');
  const [purchasedPlans, setPurchasedPlans] = useState([]);
  const userData = GetUserData();
  
  useEffect(() => {
      getWalletDetails();
      getWalletTransactions();
  },[]);
  
  useEffect(() => {
    const fetchPlansByUser = async (userId) => {
        try {
            // Fetch purchased plans by user - using new API structure
            const response = await planServices.getPlansByUser(userId);
            if (response.status === 200) {
                const purchasedPlansData = response.data.data; // Access the data array from response
                setPurchasedPlans(purchasedPlansData);
            } else {
                console.error(response.data.message);
            }
        } catch (error) {
            console.error('Error fetching the plans:', error);
        }
    };

    // Call the fetchPlansByUser function on component mount
    if (id) {
        fetchPlansByUser(id);
    }
  }, [id]);

  const getWalletDetails = async () => {
      try {
          const walletDetailsResponse = await walletServices.getWalletDetails(id);
          if(walletDetailsResponse.status === 200){
              setWalletData(walletDetailsResponse.data);
          }
      } catch (error) {
          console.error(error);
      }
  }

  const getWalletTransactions = async () => {
      try {
          const walletTransactionResponse = await walletServices.getWalletTransactions(id);
          if(walletTransactionResponse.status === 200){
              setWalletTransactionData(walletTransactionResponse.data);
          }
      } catch (error) {
          console.error(error);
      }
  }

  // Calculate current progress and growth for a plan
  const calculatePlanProgress = (plan) => {
    const today = new Date();
    const purchaseDate = new Date(plan.purchaseDate);
    const maturityDate = new Date(plan.maturityDate);
    
    const totalDuration = maturityDate - purchaseDate;
    const timeElapsed = today - purchaseDate;
    const timeElapsedInDays = Math.floor(timeElapsed / (1000 * 60 * 60 * 24));
    
    // Calculate progress percentage (0-100)
    const progress = Math.min(Math.max((timeElapsed / totalDuration) * 100, 0), 100);
    
    // Calculate current growth based on time elapsed
    let currentGrowth;
    if (progress >= 100) {
      // If plan has matured, use expected return
      currentGrowth = plan.expectedReturn;
    } else {
      // Calculate proportional growth based on time elapsed
      const profitAmount = plan.expectedReturn - plan.investmentAmount;
      const currentProfit = (profitAmount * progress) / 100;
      currentGrowth = plan.investmentAmount + currentProfit;
    }

    return {
      progress,
      currentGrowth,
      timeElapsedInDays,
      isMatured: progress >= 100
    };
  };

  return (
    <>
      <div className="body d-flex py-3"> 
        <div className="container-xxl">
          <div className="row g-3 mb-3">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3 align-items-center">
                    <div className="col-md-9 col-lg-9 col-xl-9">
                      <div className="d-flex">
                        <img className="avatar rounded-circle" src="../assets/images/profile_av.svg" alt="profile" />
                        <div className="flex-fill">
                          <p className="mb-0"><span className="font-weight-bold">{userData?.fullName}</span></p>
                          <small className="">{userData?.email}</small>
                        </div>
                      </div>
                    </div>
                    {userData?.kyc_status === "pending" && (
                      <>
                        <div className="col-md-3 col-lg-3 col-xl-3">
                          <Link to="/user/profile" title="Complete the KYC" className="btn btn-primary text-dark mb-1">Please complete your KYC!</Link>
                        </div>
                      </>
                    )}
                    {userData?.kyc_status === "in_progress" && (
                      <>
                        <div className="col-md-3 col-lg-3 col-xl-3">
                          <Link to="/user/profile" title="KYC Confirmation is in progress." className="btn btn-info text-dark mb-1">KYC confirmation is in progress!</Link>
                        </div>
                      </>
                    )}
                    {userData?.kyc_status === "completed" && (
                      <>
                        <div className="col-md-3 col-lg-3 col-xl-3">
                          <Link to="/user/profile" title="KYC Confirmation is in progress." className="btn btn-success text-dark mb-1">KYC is completed!</Link>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Investment Progress Cards */}
          <div className="row g-4 mb-4">
            {purchasedPlans && purchasedPlans.map((plan, index) => {
              const { progress, currentGrowth, isMatured } = calculatePlanProgress(plan);
              const profitPercentage = ((currentGrowth - plan.investmentAmount) / plan.investmentAmount * 100);
              
              return (
                <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12" key={plan._id}>
                  <div className="card h-100 shadow-sm border-0">
                    <div className="card-header bg-gradient border-0 py-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold text-dark">{plan.planCategory}</h6>
                        <span className={`badge ${isMatured ? 'bg-success' : 'bg-primary'} rounded-pill`}>
                          {isMatured ? 'Matured' : 'Active'}
                        </span>
                      </div>
                      <small className="text-muted text-capitalize">{plan.planType} Plan</small>
                    </div>
                    <div className="card-body">
                      <div className="row text-center mb-3">
                        <div className="col-6">
                          <div className="border-end">
                            <h5 className="mb-0 fw-bold text-success">${currentGrowth.toFixed(2)}</h5>
                            <small className="text-muted">Current Value</small>
                          </div>
                        </div>
                        <div className="col-6">
                          <h5 className="mb-0 fw-bold text-primary">${plan.investmentAmount}</h5>
                          <small className="text-muted">Invested</small>
                        </div>
                      </div>
                      
                      <div className="text-center mb-3">
                        <span className="badge bg-light text-success fs-6 px-3 py-2">
                          <i className="fa fa-trending-up me-1"></i>
                          +{profitPercentage.toFixed(2)}%
                        </span>
                      </div>

                      <InvestmentProgress
                        plan={plan}
                        investedAmount={plan.investmentAmount}
                        startDate={plan.purchaseDate}
                        maturityDate={plan.maturityDate}
                        expectedReturn={plan.expectedReturn}
                        lockInPeriod={plan.lockInPeriod}
                        profitPercentage={plan.profitPercentage}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-gradient border-0 py-3">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="fa fa-wallet me-2"></i>Balance Overview
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-lg-8">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="text-center p-3 bg-light rounded">
                            <h4 className="mb-1 fw-bold text-primary">${walletData.balance || 0}</h4>
                            <small className="text-muted text-uppercase">Account Balance</small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-center p-3 bg-light rounded">
                            <h4 className="mb-1 fw-bold text-success">${walletData.total_earned || 0}</h4>
                            <small className="text-muted text-uppercase">Total Earned</small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="text-center p-3 bg-light rounded">
                            <h4 className="mb-1 fw-bold text-danger">${walletData.total_spent || 0}</h4>
                            <small className="text-muted text-uppercase">Total Spent</small>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <DonutChart
                        series={[
                          walletData.balance || 0, 
                          walletData.total_earned || 0, 
                          walletData.total_spent || 0
                        ]}
                        labels={['Balance', 'Total Earned', 'Total Spent']}
                        colors={['var(--chart-color1)', 'var(--chart-color2)', 'var(--chart-color3)']}
                        id="apex-simple-donut"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-gradient border-0 py-3">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="fa fa-history me-2"></i>Recent Transactions
                  </h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="border-0">Date</th>
                          <th className="border-0">Type</th>
                          <th className="border-0">Description</th>
                          <th className="border-0">Amount</th>
                          <th className="border-0">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {walletTransactionData && walletTransactionData.length > 0 ? (
                          walletTransactionData.slice(0, 10).map((transaction) => {
                            const isWallet = transaction.transaction_source === 'wallet';
                            const date = new Date(transaction.created_at || transaction.createdAt);
                            const isCredit = transaction.transaction_type === 'credit';

                            return (
                              <tr key={transaction._id}>
                                <td>
                                  <small className="text-muted">
                                    {date.toLocaleDateString()}
                                  </small>
                                  <br />
                                  <small className="text-muted">
                                    {date.toLocaleTimeString()}
                                  </small>
                                </td>

                                <td>
                                  {isWallet ? (
                                    <span className={`badge rounded-pill ${isCredit ? 'bg-success' : 'bg-danger'}`}>
                                      {isCredit ? 'Credit' : 'Debit'}
                                    </span>
                                  ) : (
                                    <span className="badge rounded-pill bg-warning text-dark">Topup</span>
                                  )}
                                </td>

                                <td>
                                  <span className="fw-medium">
                                    {isWallet
                                      ? (transaction.description || 'Wallet Transaction')
                                      : 'Topup Transaction'}
                                  </span>
                                </td>

                                <td>
                                  <span className={`fw-bold ${isWallet ? (isCredit ? 'text-success' : 'text-danger') : 'text-info'}`}>
                                    {isWallet
                                      ? `${isCredit ? '+' : '-'}$${transaction.amount.toFixed(2)}`
                                      : `+$${transaction.amount.toFixed(2)}`}
                                  </span>
                                </td>

                                <td>
                                  <span className={`badge rounded-pill ${
                                    isWallet
                                      ? 'bg-success'
                                      : transaction.status === 'pending'
                                      ? 'bg-warning text-dark'
                                      : transaction.status === 'approved'
                                      ? 'bg-success'
                                      : 'bg-danger'
                                  }`}>
                                    {isWallet ? 'Completed' : transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-4">
                              <div className="text-muted">
                                <i className="fa fa-inbox fa-2x mb-2"></i>
                                <p>No transactions found</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;